import { type FC, useCallback, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import StyledTooltip from "src/components/reusable/StyledTooltip/StyledTooltip";
import type { SalesAttributionSeriesKey, SalesAttributionTrendPoint } from "../utils/SalesAttributionTypes";
import ReconciliationChart, {
  RECONCILIATION_SERIES_GROUPS,
  SERIES_BY_KEY,
  seriesChipLabel,
  seriesGroupLabel,
  seriesMatchesPlatformFilter,
} from "./ReconciliationChart";
import { panelTooltip, SalesAttributionPanel, shadeColor } from "./SalesAttributionShared";

const PAGE_SIZE = 14;

const DEFAULT_SERIES: SalesAttributionSeriesKey[] = RECONCILIATION_SERIES_GROUPS.flatMap(
  group => group.keys
);

interface SalesAttributionReconciliationSectionProps {
  trend: SalesAttributionTrendPoint[];
  platformFilter: string;
}

export const SalesAttributionReconciliationSection: FC<
  SalesAttributionReconciliationSectionProps
> = ({ trend, platformFilter }) => {
  const { t } = useTranslation();
  const [activeSeries, setActiveSeries] =
    useState<SalesAttributionSeriesKey[]>(DEFAULT_SERIES);
  const [page, setPage] = useState(0);

  const visibleGroups = useMemo(
    () =>
      RECONCILIATION_SERIES_GROUPS.map(group => ({
        ...group,
        keys: group.keys.filter(key => seriesMatchesPlatformFilter(key, platformFilter)),
      })).filter(group => group.keys.length > 0),
    [platformFilter]
  );

  const chartSeries = useMemo(
    () => activeSeries.filter(key => seriesMatchesPlatformFilter(key, platformFilter)),
    [activeSeries, platformFilter]
  );

  const pageCount = Math.max(1, Math.ceil(trend.length / PAGE_SIZE));
  // Guards against a stale page index when a shorter date range is selected.
  const safePage = Math.min(page, pageCount - 1);

  const pagedTrend = useMemo(
    () => trend.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE),
    [trend, safePage]
  );

  const toggleSeries = useCallback((key: SalesAttributionSeriesKey) => {
    setActiveSeries(current =>
      current.includes(key)
        ? current.filter(activeKey => activeKey !== key)
        : [...current, key]
    );
  }, []);

  const goToPreviousPage = useCallback(
    () => setPage(current => Math.max(0, current - 1)),
    []
  );
  const goToNextPage = useCallback(
    () => setPage(current => Math.min(pageCount - 1, current + 1)),
    [pageCount]
  );

  return (
    <SalesAttributionPanel
      title={t("salesAttribution.ui.panelTitles.reconciliation", { defaultValue: "Reconciliation" })}
      definition={panelTooltip(
        t,
        "reconciliation",
        "Compare key metrics across Shopify, GA4, Google Ads, and Meta Ads to identify differences in reporting and attribution. Use the metric chips to show or hide data sources and compare trends over time."
      )}
      dataPdfBlock="attribution-reconciliation"
    >
      <div className="sales-attribution__series-groups">
        {visibleGroups.map(group => {
          const groupLabel = seriesGroupLabel(t, group.metric);
          return (
          <div
            key={group.metric}
            role="group"
            aria-label={groupLabel}
            className="sales-attribution__series-group"
          >
            <span className="sales-attribution__series-group-label" aria-hidden>
              {groupLabel.toUpperCase()}
            </span>
            <div className="sales-attribution__series-chips">
              {group.keys.map(key => {
                const isActive = activeSeries.includes(key);
                const series = SERIES_BY_KEY.get(key);
                const seriesTooltip = series
                  ? t(`salesAttribution.tooltips.series.${key}`, {
                      defaultValue: series.descriptionDefault,
                    })
                  : undefined;
                const color = series?.color;
                // Chip is outlined + tinted in its own source color when active,
                // and shown as a muted outline in that same color when inactive —
                // mirrors the reconciliation legend colors used in the chart itself.
                const chipStyle = color
                  ? isActive
                    ? {
                        borderColor: color,
                        color,
                        backgroundColor: shadeColor(color, 0.85),
                      }
                    : {
                        borderColor: shadeColor(color, 0.55),
                        color: shadeColor(color, -0.05),
                        backgroundColor: "transparent",
                      }
                  : undefined;
                return (
                  <StyledTooltip
                    key={key}
                    content={seriesTooltip}
                    className="sales-attribution__series-chip-tooltip"
                  >
                    <button
                      type="button"
                      role="switch"
                      aria-checked={isActive}
                      className={`sales-attribution__series-chip${
                        isActive ? " sales-attribution__series-chip--active" : ""
                      }`}
                      style={chipStyle}
                      onClick={() => toggleSeries(key)}
                    >
                      {color ? (
                        <span
                          className="sales-attribution__series-chip-dot"
                          style={{ background: color }}
                          aria-hidden
                        />
                      ) : null}
                      {series ? seriesChipLabel(t, key) : key}
                    </button>
                  </StyledTooltip>
                );
              })}
            </div>
          </div>
          );
        })}
      </div>

      {chartSeries.length === 0 ? (
        <p className="sales-attribution__empty">
          {t("salesAttribution.ui.emptySeriesSelection", {
            defaultValue: "Select at least one series above to plot the reconciliation chart.",
          })}
        </p>
      ) : (
        <ReconciliationChart data={pagedTrend} activeSeries={chartSeries} />
      )}

      {pageCount > 1 ? (
        <div className="sales-attribution__pagination">
          <span className="sales-attribution__pagination-label">
            {t("salesAttribution.ui.pagination.page", { defaultValue: "Page" })} {safePage + 1}{" "}
            {t("salesAttribution.ui.pagination.of", { defaultValue: "Of" })} {pageCount}
          </span>
          <div className="sales-attribution__pagination-buttons">
            <button
              type="button"
              className="sales-attribution__pagination-button"
              onClick={goToPreviousPage}
              disabled={safePage === 0}
              aria-label={t("salesAttribution.ui.pagination.previousDays", {
                defaultValue: "Previous days",
              })}
            >
              <ChevronLeft size={16} aria-hidden />
            </button>
            <StyledTooltip
              content={t("salesAttribution.ui.pagination.nextPageTooltip", {
                defaultValue: "This page shows 10 dates of the month per page.",
              })}
              className="sales-attribution__pagination-next-tooltip"
            >
              <button
                type="button"
                className="sales-attribution__pagination-button"
                onClick={goToNextPage}
                disabled={safePage === pageCount - 1}
                aria-label={t("salesAttribution.ui.pagination.nextDays", { defaultValue: "Next days" })}
              >
                <ChevronRight size={16} aria-hidden />
              </button>
            </StyledTooltip>
          </div>
        </div>
      ) : null}
    </SalesAttributionPanel>
  );
};

export default SalesAttributionReconciliationSection;
